import React, { useState } from "react";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, Legend
} from "recharts";
import {
  FiDollarSign, FiFileText, FiActivity, FiLayers, FiPlus,
  FiArrowUpRight, FiZap, FiBell, FiPackage, FiUsers,
  FiTrendingUp, FiCheckCircle, FiAlertCircle, FiClock,
  FiCalendar, FiArrowDownLeft, FiGlobe, FiBriefcase,
  FiArrowUp, FiArrowDown, FiMoreHorizontal, FiRefreshCw,
  FiShield, FiCpu, FiDatabase, FiBarChart2
} from "react-icons/fi";
import { Link } from "react-router-dom";

/* ═══════════════════════════════════════════
   STYLES
═══════════════════════════════════════════ */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;1,400&display=swap');

:root {
  --t:    #0a5556;
  --t2:   #0c7072;
  --t3:   #14b8a6;
  --t4:   #5eead4;
  --t5:   #ccf1ee;
  --gold: #FFD166;
  --gold2:#e6a800;
  --bg:   #edf7f7;
  --surface: rgba(255,255,255,0.94);
  --border:  rgba(10,85,86,0.11);
  --danger:  #ef4444;
  --success: #10b981;
  --warn:    #f59e0b;
  --info:    #3b82f6;
  --text:    #07282a;
  --muted:   #5a8080;
  --r:    18px;
  --r-sm: 10px;
  --sh:   0 2px 16px rgba(10,85,86,0.08), 0 1px 3px rgba(10,85,86,0.05);
  --sh-md:0 8px 32px rgba(10,85,86,0.11), 0 2px 8px rgba(10,85,86,0.06);
  --sh-lg:0 20px 60px rgba(10,85,86,0.14), 0 4px 16px rgba(10,85,86,0.08);
}

@keyframes hm-up      { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
@keyframes hm-in      { from{opacity:0} to{opacity:1} }
@keyframes hm-float   { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-16px)} }
@keyframes hm-float2  { 0%,100%{transform:translateY(0)} 50%{transform:translateY(12px)} }
@keyframes hm-pulse   { 0%,100%{box-shadow:0 0 0 0 rgba(16,185,129,0.5)} 60%{box-shadow:0 0 0 8px rgba(16,185,129,0)} }
@keyframes hm-shimmer { 0%{background-position:-600px 0} 100%{background-position:600px 0} }
@keyframes hm-spin    { to{transform:rotate(360deg)} }
@keyframes hm-count   { from{opacity:0;transform:scale(0.7)} to{opacity:1;transform:scale(1)} }
@keyframes hm-bar-in  { from{width:0} to{width:var(--w)} }
@keyframes hm-ticker  { 0%{transform:translateX(0)} 100%{transform:translateX(-50%)} }

/* ── PAGE ── */
.hm-page { font-family:'DM Sans',sans-serif; background:var(--bg); min-height:100vh; position:relative; overflow-x:hidden; }
.hm-blob { position:fixed;border-radius:50%;pointer-events:none;z-index:0; }
.hm-blob-1 { width:500px;height:500px;top:-150px;right:-120px; background:radial-gradient(circle at 35% 35%,rgba(10,85,86,0.10),transparent 65%); animation:hm-float 12s ease-in-out infinite; }
.hm-blob-2 { width:380px;height:380px;bottom:40px;left:-80px; background:radial-gradient(circle at 65% 65%,rgba(20,184,166,0.09),transparent 65%); animation:hm-float2 15s ease-in-out infinite; }
.hm-blob-3 { width:220px;height:220px;top:40%;right:8%; background:radial-gradient(circle,rgba(255,209,102,0.07),transparent 68%); animation:hm-float 10s ease-in-out infinite 3s; }
.hm-grid  { position:fixed;inset:0;pointer-events:none;z-index:0; background-image:linear-gradient(rgba(10,85,86,0.022) 1px,transparent 1px),linear-gradient(90deg,rgba(10,85,86,0.022) 1px,transparent 1px);background-size:58px 58px; }

.hm-wrap { position:relative;z-index:1;padding:22px 24px 64px; animation:hm-in 0.4s ease; }

/* ── TICKER ── */
.hm-ticker-bar { background:linear-gradient(90deg,var(--t),var(--t2));border-radius:12px;padding:9px 18px;display:flex;align-items:center;gap:14px;margin-bottom:22px;overflow:hidden;animation:hm-up 0.4s ease; }
.hm-ticker-label { font-family:'Sora',sans-serif;font-size:10px;font-weight:700;color:rgba(255,255,255,0.65);text-transform:uppercase;letter-spacing:.08em;white-space:nowrap;flex-shrink:0;display:flex;align-items:center;gap:6px; }
.hm-ticker-dot { width:6px;height:6px;border-radius:50%;background:var(--gold);animation:hm-pulse 2s ease infinite; }
.hm-ticker-track { overflow:hidden;flex:1; }
.hm-ticker-items { display:flex;gap:40px;white-space:nowrap;animation:hm-ticker 24s linear infinite; }
.hm-ticker-item  { font-size:12px;color:rgba(255,255,255,0.88);display:flex;align-items:center;gap:6px;flex-shrink:0; }
.hm-ticker-item .up   { color:#4ade80; }
.hm-ticker-item .down { color:#f87171; }

/* ── PAGE HEADER ── */
.hm-phd { display:flex;align-items:center;justify-content:space-between;margin-bottom:24px;animation:hm-up 0.5s ease 0.05s both; }
.hm-phd-left h1 { font-family:'Sora',sans-serif;font-size:24px;font-weight:800;color:var(--text);letter-spacing:-.04em;margin:0 0 4px;line-height:1; }
.hm-phd-left p  { font-size:12.5px;color:var(--muted);margin:0;display:flex;align-items:center;gap:6px; }
.hm-status-dot  { width:7px;height:7px;border-radius:50%;background:var(--success);animation:hm-pulse 2s ease infinite; }
.hm-date-chip   { display:inline-flex;align-items:center;gap:6px;padding:5px 12px;border-radius:50px;background:rgba(10,85,86,0.07);border:1px solid var(--border);font-size:11.5px;color:var(--t);font-family:'Sora',sans-serif;font-weight:600; }
.hm-phd-right   { display:flex;align-items:center;gap:10px; }
.hm-icon-btn    { width:38px;height:38px;border-radius:11px;border:1.5px solid var(--border);background:var(--surface);display:flex;align-items:center;justify-content:center;color:var(--t);cursor:pointer;transition:all .2s;position:relative;backdrop-filter:blur(12px); }
.hm-icon-btn:hover { background:rgba(10,85,86,0.06);transform:translateY(-1px);box-shadow:var(--sh); }
.hm-notif-dot { width:7px;height:7px;border-radius:50%;background:var(--danger);position:absolute;top:6px;right:6px;border:1.5px solid #fff; }
.hm-cta-btn { display:inline-flex;align-items:center;gap:7px;padding:9px 18px;border-radius:11px;border:none;background:linear-gradient(135deg,var(--t),var(--t3));color:#fff;font-family:'Sora',sans-serif;font-size:12.5px;font-weight:700;cursor:pointer;box-shadow:0 6px 20px rgba(10,85,86,0.30);transition:all .25s;text-decoration:none; }
.hm-cta-btn:hover { transform:translateY(-2px);box-shadow:0 12px 28px rgba(10,85,86,0.38);color:#fff; }

/* ── SECTION LABEL ── */
.hm-sec-lbl { font-family:'Sora',sans-serif;font-size:11px;font-weight:700;color:var(--muted);text-transform:uppercase;letter-spacing:.09em;margin-bottom:12px;display:flex;align-items:center;gap:8px; }
.hm-sec-lbl::after { content:'';flex:1;height:1px;background:var(--border); }

/* ── CARD ── */
.hm-card { background:var(--surface);backdrop-filter:blur(24px);border:1px solid rgba(255,255,255,0.88);border-radius:var(--r);box-shadow:var(--sh);overflow:hidden;position:relative;transition:box-shadow .25s,transform .25s; }
.hm-card:hover { box-shadow:var(--sh-md);transform:translateY(-2px); }
.hm-card::before { content:'';position:absolute;top:0;left:0;right:0;height:3px;background:linear-gradient(90deg,var(--t) 0%,var(--t3) 55%,var(--gold) 100%); }

/* ── KPI CARDS ── */
.hm-kpi-grid { display:grid;grid-template-columns:repeat(4,1fr);gap:16px;margin-bottom:20px;animation:hm-up 0.5s ease 0.1s both; }
.hm-kpi { padding:20px;position:relative;overflow:hidden; }
.hm-kpi-accent { position:absolute;top:-22px;right:-22px;width:88px;height:88px;border-radius:50%;opacity:0.08; }
.hm-kpi-accent.blue   { background:#3b82f6; }
.hm-kpi-accent.teal   { background:var(--t3); }
.hm-kpi-accent.gold   { background:var(--gold); }
.hm-kpi-accent.red    { background:var(--danger); }
.hm-kpi-accent.purple { background:#8b5cf6; }
.hm-kpi-icon  { width:40px;height:40px;border-radius:12px;display:flex;align-items:center;justify-content:center;font-size:17px;margin-bottom:14px; }
.hm-kpi-icon.teal   { background:rgba(10,85,86,0.10);color:var(--t); }
.hm-kpi-icon.gold   { background:rgba(255,209,102,0.18);color:var(--gold2); }
.hm-kpi-icon.danger { background:rgba(239,68,68,0.10);color:var(--danger); }
.hm-kpi-icon.purple { background:rgba(139,92,246,0.10);color:#8b5cf6; }
.hm-kpi-icon.blue   { background:rgba(59,130,246,0.10);color:var(--info); }
.hm-kpi-label { font-size:11.5px;color:var(--muted);font-weight:500;margin-bottom:5px; }
.hm-kpi-val   { font-family:'Sora',sans-serif;font-size:22px;font-weight:800;color:var(--text);letter-spacing:-.03em;line-height:1;margin-bottom:8px;animation:hm-count 0.6s ease both; }
.hm-kpi-trend { display:inline-flex;align-items:center;gap:4px;font-size:11.5px;font-weight:600;padding:3px 8px;border-radius:50px; }
.hm-kpi-trend.up   { background:rgba(16,185,129,0.10);color:var(--success); }
.hm-kpi-trend.down { background:rgba(239,68,68,0.10);color:var(--danger); }
.hm-kpi-sub   { font-size:11px;color:var(--muted);margin-top:4px; }

/* ── CHART CARD ── */
.hm-chart-header { display:flex;align-items:flex-start;justify-content:space-between;padding:20px 22px 0; }
.hm-chart-title  { font-family:'Sora',sans-serif;font-size:14px;font-weight:700;color:var(--text);margin:0 0 3px; }
.hm-chart-sub    { font-size:11.5px;color:var(--muted);margin:0; }
.hm-chart-body   { padding:12px 8px 16px; }
.hm-period-tabs  { display:flex;gap:4px;background:rgba(10,85,86,0.05);border-radius:8px;padding:3px; }
.hm-period-tab   { padding:4px 10px;border:none;background:transparent;border-radius:6px;font-size:11.5px;font-weight:600;color:var(--muted);cursor:pointer;transition:all .18s;font-family:'DM Sans',sans-serif; }
.hm-period-tab.active { background:#fff;color:var(--t);box-shadow:0 1px 6px rgba(10,85,86,0.12); }

.hm-insight-chip { display:inline-flex;align-items:center;gap:6px;padding:6px 12px;border-radius:8px;background:rgba(10,85,86,0.05);border:1px solid var(--border);font-size:11.5px;color:var(--t);margin:0 22px 14px; }
.hm-insight-chip strong { color:var(--t);font-family:'Sora',sans-serif; }

/* ── PIE LEGEND ── */
.hm-pie-legend { padding:0 20px 18px; }
.hm-pie-row { display:flex;align-items:center;justify-content:space-between;padding:7px 0;border-bottom:1px solid rgba(10,85,86,0.06); }
.hm-pie-row:last-child { border-bottom:none; }
.hm-pie-name { display:flex;align-items:center;gap:8px;font-size:12.5px;color:#2d4a4a; }
.hm-pie-dot  { width:8px;height:8px;border-radius:50%;flex-shrink:0; }
.hm-pie-bar-wrap { flex:1;margin:0 12px;height:4px;background:rgba(10,85,86,0.07);border-radius:50px;overflow:hidden; }
.hm-pie-bar  { height:100%;border-radius:50px;transition:width 1s ease; }
.hm-pie-pct  { font-family:'Sora',sans-serif;font-size:12px;font-weight:700;color:var(--t);min-width:30px;text-align:right; }

/* ── TIMELINE ── */
.hm-timeline { padding:0 20px 18px; }
.hm-tl-item  { display:flex;align-items:flex-start;gap:12px;padding:10px 0;border-bottom:1px solid rgba(10,85,86,0.06);position:relative; }
.hm-tl-item:last-child { border-bottom:none; }
.hm-tl-date  { min-width:42px;text-align:center; }
.hm-tl-day   { font-family:'Sora',sans-serif;font-size:16px;font-weight:800;color:var(--t);line-height:1; }
.hm-tl-mon   { font-size:10px;color:var(--muted);font-weight:600;text-transform:uppercase;letter-spacing:.04em; }
.hm-tl-divider { width:1px;background:var(--border);align-self:stretch;flex-shrink:0;margin-top:4px; }
.hm-tl-body  { flex:1; }
.hm-tl-title { font-size:13px;font-weight:600;color:var(--text);margin-bottom:2px; }
.hm-tl-meta  { font-size:11px;color:var(--muted);display:flex;align-items:center;gap:6px; }
.hm-tl-tag   { padding:2px 8px;border-radius:50px;font-size:10.5px;font-weight:700;font-family:'Sora',sans-serif; }
.hm-tl-tag.tax     { background:rgba(239,68,68,0.10);color:var(--danger); }
.hm-tl-tag.admin   { background:rgba(59,130,246,0.10);color:var(--info); }
.hm-tl-tag.finance { background:rgba(10,85,86,0.09);color:var(--t); }

/* ── TRANSACTIONS TABLE ── */
.hm-tbl-wrap { overflow-x:auto; }
.hm-tbl { width:100%;border-collapse:collapse;font-family:'DM Sans',sans-serif; }
.hm-tbl thead tr { background:linear-gradient(135deg,rgba(10,85,86,0.04),rgba(20,184,166,0.02)); }
.hm-tbl thead th { padding:11px 16px;font-family:'Sora',sans-serif;font-size:10px;font-weight:700;color:var(--muted);letter-spacing:.07em;text-transform:uppercase;border-bottom:1.5px solid var(--border); }
.hm-tbl tbody tr { border-bottom:1px solid rgba(10,85,86,0.055);transition:background .15s; }
.hm-tbl tbody tr:last-child { border-bottom:none; }
.hm-tbl tbody tr:hover { background:rgba(10,85,86,0.025); }
.hm-tbl td { padding:12px 16px;font-size:13px;vertical-align:middle; }
.hm-inv-id { font-family:'Sora',sans-serif;font-weight:700;font-size:12px;color:var(--t);background:rgba(10,85,86,0.07);border:1px solid var(--border);padding:3px 9px;border-radius:6px;display:inline-flex;align-items:center;gap:5px; }
.hm-client-name { font-weight:600;color:var(--text); }
.hm-client-sub  { font-size:11px;color:var(--muted); }
.hm-amount { font-family:'Sora',sans-serif;font-weight:700;color:var(--text);font-size:13.5px; }

.hm-status { display:inline-flex;align-items:center;gap:5px;padding:4px 10px;border-radius:50px;font-size:11px;font-weight:700;font-family:'Sora',sans-serif; }
.hm-status.settled { background:rgba(16,185,129,0.10);color:var(--success); }
.hm-status.pending { background:rgba(245,158,11,0.12);color:var(--warn); }
.hm-status.overdue { background:rgba(239,68,68,0.10);color:var(--danger); }
.hm-status-ring { width:6px;height:6px;border-radius:50%; }
.hm-status.settled .hm-status-ring { background:var(--success); }
.hm-status.pending .hm-status-ring { background:var(--warn); }
.hm-status.overdue .hm-status-ring { background:var(--danger); }

.hm-tbl-action { width:28px;height:28px;border-radius:7px;border:1.5px solid var(--border);background:transparent;color:var(--t);display:flex;align-items:center;justify-content:center;cursor:pointer;transition:all .18s;font-size:13px; }
.hm-tbl-action:hover { background:rgba(10,85,86,0.07);transform:scale(1.08); }

/* ── SMART TOOLS ── */
.hm-tools-card { background:linear-gradient(135deg,var(--t) 0%,var(--t2) 60%,#0d8080 100%);position:relative;overflow:hidden; }
.hm-tools-card::before { content:'';position:absolute;top:0;left:0;right:0;height:3px;background:linear-gradient(90deg,var(--gold),rgba(255,209,102,0.4)); }
.hm-tools-bg-circle { position:absolute;border-radius:50%;pointer-events:none; }
.hm-tools-bg-circle-1 { width:180px;height:180px;top:-60px;right:-40px;background:rgba(255,255,255,0.04); }
.hm-tools-bg-circle-2 { width:120px;height:120px;bottom:-30px;left:30px;background:rgba(255,255,255,0.03); }
.hm-tools-hd { padding:22px 22px 14px;position:relative;z-index:1; }
.hm-tools-title { font-family:'Sora',sans-serif;font-size:15px;font-weight:800;color:#fff;margin:0 0 4px; }
.hm-tools-sub   { font-size:12px;color:rgba(255,255,255,0.6);margin:0; }
.hm-tools-grid  { display:grid;grid-template-columns:1fr 1fr;gap:10px;padding:0 18px 20px;position:relative;z-index:1; }
.hm-tool-tile   { padding:14px;border-radius:12px;background:rgba(255,255,255,0.08);border:1px solid rgba(255,255,255,0.12);cursor:pointer;transition:all .2s;display:flex;align-items:center;gap:11px; }
.hm-tool-tile:hover { background:rgba(255,255,255,0.15);transform:translateY(-2px); }
.hm-tool-icon  { width:34px;height:34px;border-radius:9px;background:rgba(255,255,255,0.12);display:flex;align-items:center;justify-content:center;font-size:15px;color:#fff;flex-shrink:0; }
.hm-tool-label { font-family:'Sora',sans-serif;font-size:12px;font-weight:700;color:#fff; }
.hm-tool-sub   { font-size:10.5px;color:rgba(255,255,255,0.55);margin-top:1px; }

/* ── SYSTEM HEALTH ── */
.hm-health-grid { display:grid;grid-template-columns:1fr 1fr;gap:12px;padding:18px 20px 0; }
.hm-health-tile { padding:14px 16px;border-radius:12px;background:rgba(10,85,86,0.04);border:1.5px solid var(--border); }
.hm-health-label { font-size:11px;color:var(--muted);margin-bottom:6px; }
.hm-health-val   { font-family:'Sora',sans-serif;font-size:20px;font-weight:800;display:flex;align-items:center;gap:6px; }
.hm-health-val.green { color:var(--success); }
.hm-health-val.blue  { color:var(--info); }
.hm-health-sub   { font-size:10.5px;color:var(--muted);margin-top:3px; }

.hm-monitor { margin:16px 20px 20px;padding:14px 16px;border-radius:12px;background:rgba(16,185,129,0.04);border:1.5px solid rgba(16,185,129,0.18);display:flex;align-items:flex-start;gap:12px; }
.hm-monitor-pulse { width:32px;height:32px;border-radius:50%;background:rgba(16,185,129,0.12);display:flex;align-items:center;justify-content:center;flex-shrink:0;position:relative; }
.hm-monitor-pulse::after { content:'';position:absolute;inset:0;border-radius:50%;background:rgba(16,185,129,0.25);animation:hm-pulse 2s ease infinite; }
.hm-monitor-dot { width:10px;height:10px;border-radius:50%;background:var(--success); }
.hm-monitor-text { font-size:12px;color:#2d5050;line-height:1.6; }
.hm-monitor-text strong { color:var(--t);font-weight:700; }

/* ── LAYOUT HELPERS ── */
.hm-row { display:grid;gap:18px;margin-bottom:20px; }
.hm-row-3-2 { grid-template-columns:3fr 2fr; }
.hm-row-4-3 { grid-template-columns:4fr 3fr; }
.hm-row-5-4 { grid-template-columns:5fr 4fr; }
.hm-row-2   { grid-template-columns:1fr 1fr; }
.hm-row-3-4 { grid-template-columns:3fr 4fr; }
.hm-row-4-4 { grid-template-columns:1fr 1fr; }

/* ── CHART TABS ── */
.hm-live-badge { display:inline-flex;align-items:center;gap:5px;padding:3px 9px;border-radius:50px;background:rgba(16,185,129,0.10);border:1px solid rgba(16,185,129,0.22);font-size:10.5px;font-weight:700;color:var(--success);font-family:'Sora',sans-serif; }
.hm-live-badge-dot { width:5px;height:5px;border-radius:50%;background:var(--success);animation:hm-pulse 2s ease infinite; }

/* ── CUSTOM TOOLTIP ── */
.hm-tooltip { background:#fff;border:1px solid var(--border);border-radius:12px;padding:10px 14px;box-shadow:var(--sh-md);font-family:'DM Sans',sans-serif; }
.hm-tooltip-label { font-family:'Sora',sans-serif;font-size:11px;font-weight:700;color:var(--muted);text-transform:uppercase;letter-spacing:.05em;margin-bottom:6px; }
.hm-tooltip-row   { display:flex;align-items:center;gap:8px;font-size:12.5px;margin-bottom:2px; }
.hm-tooltip-dot   { width:7px;height:7px;border-radius:50%; }

/* ── RESPONSIVE ── */
@media(max-width:1200px) { .hm-kpi-grid{grid-template-columns:1fr 1fr;} .hm-row-3-2,.hm-row-4-3,.hm-row-5-4{grid-template-columns:1fr;} .hm-row-3-4{grid-template-columns:1fr;} }
@media(max-width:800px)  { .hm-kpi-grid{grid-template-columns:1fr 1fr;} .hm-row-2,.hm-row-4-4{grid-template-columns:1fr;} .hm-wrap{padding:14px 14px 48px;} .hm-tools-grid{grid-template-columns:1fr 1fr;} }
@media(max-width:500px)  { .hm-kpi-grid{grid-template-columns:1fr;} }
`;

/* ═══════════════════════════════════════════
   DATA
═══════════════════════════════════════════ */
const performanceData = [
  { name: "Mon", income: 4000, expense: 2400 },
  { name: "Tue", income: 5000, expense: 1398 },
  { name: "Wed", income: 9800, expense: 3000 },
  { name: "Thu", income: 6908, expense: 2780 },
  { name: "Fri", income: 8800, expense: 1890 },
  { name: "Sat", income: 11000, expense: 2390 },
  { name: "Sun", income: 13000, expense: 2100 },
];

const categoryData = [
  { name: "Operations", value: 40, color: "#0a5556" },
  { name: "Marketing",  value: 30, color: "#14b8a6" },
  { name: "Payroll",    value: 20, color: "#FFD166" },
  { name: "Inventory",  value: 10, color: "#8b5cf6" },
];

const transactions = [
  { id:"INV-8821", name:"Aditi Tech Pvt Ltd",    sub:"Mumbai, MH",    amt:"₹42,000",   status:"settled", date:"23 Oct" },
  { id:"QUO-0012", name:"Vikas Enterprises",      sub:"Delhi, DL",     amt:"₹1,12,000", status:"pending", date:"21 Oct" },
  { id:"INV-8820", name:"Rahul Sharma",            sub:"Pune, MH",      amt:"₹12,500",   status:"overdue", date:"18 Oct" },
  { id:"INV-8819", name:"Global Logistics Ltd",   sub:"Bangalore, KA", amt:"₹98,200",   status:"settled", date:"17 Oct" },
];

const deadlines = [
  { title:"GST Filing Q3",      date:"24",mon:"Oct", type:"tax",     desc:"Due in 3 days" },
  { title:"Payroll Processing", date:"28",mon:"Oct", type:"admin",   desc:"28 employees" },
  { title:"Vendor Payouts",     date:"02",mon:"Nov", type:"finance", desc:"14 pending bills" },
];

const tickerItems = [
  { label:"INV-8821", val:"₹42,000", up:true },
  { label:"GST Filing", val:"Oct 24", up:null },
  { label:"Revenue MTD", val:"₹6.8L", up:true },
  { label:"Pending Invoices", val:"12", up:false },
  { label:"Stock Valuation", val:"₹45.2L", up:true },
  { label:"Server Uptime", val:"99.98%", up:true },
];

/* ═══════════════════════════════════════════
   CUSTOM TOOLTIP
═══════════════════════════════════════════ */
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="hm-tooltip">
      <div className="hm-tooltip-label">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="hm-tooltip-row">
          <div className="hm-tooltip-dot" style={{ background: p.color || p.stroke }} />
          <span style={{ color: "#2d4a4a", fontWeight: 500 }}>{p.name}:</span>
          <span style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, color: "#07282a" }}>
            ₹{Number(p.value).toLocaleString("en-IN")}
          </span>
        </div>
      ))}
    </div>
  );
};

/* ═══════════════════════════════════════════
   COMPONENT
═══════════════════════════════════════════ */
function Home() {
  const [activePeriod, setActivePeriod] = useState("Weekly");
  const today = new Date().toLocaleDateString("en-IN", { weekday:"long", day:"numeric", month:"long" });

  return (
    <>
      <style>{CSS}</style>
      <Topbar />
      <Sidebar />

      <main className="main-wrapper">
        <div className="hm-page">
          <div className="hm-blob hm-blob-1"/>
          <div className="hm-blob hm-blob-2"/>
          <div className="hm-blob hm-blob-3"/>
          <div className="hm-grid"/>

          <div className="hm-wrap">

            {/* ── Ticker Bar ── */}
            <div className="hm-ticker-bar">
              <div className="hm-ticker-label">
                <div className="hm-ticker-dot"/>
                Live Feed
              </div>
              <div className="hm-ticker-track">
                <div className="hm-ticker-items">
                  {[...tickerItems, ...tickerItems].map((t, i) => (
                    <span key={i} className="hm-ticker-item">
                      <FiActivity size={10} style={{ opacity: 0.6 }} />
                      <strong style={{ color: "rgba(255,255,255,0.65)", fontFamily: "'Sora',sans-serif", fontSize: 10, letterSpacing: ".04em" }}>{t.label}:</strong>
                      {t.val}
                      {t.up === true  && <span className="up">   ▲</span>}
                      {t.up === false && <span className="down"> ▼</span>}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Page Header ── */}
            <div className="hm-phd">
              <div className="hm-phd-left">
                <h1>Financial Command Center</h1>
                <p>
                  <span className="hm-status-dot"/>
                  All systems operational &nbsp;·&nbsp;
                  <strong style={{ color: "var(--t)", fontWeight: 700 }}>Q4 Growth: +14.2%</strong>
                </p>
              </div>
              <div className="hm-phd-right">
                <div className="hm-date-chip"><FiCalendar size={12}/>{today}</div>
                <div className="hm-icon-btn"><FiGlobe size={15}/><div className="hm-notif-dot"/></div>
                <div className="hm-icon-btn"><FiBell size={15}/><div className="hm-notif-dot"/></div>
                <Link to="/invoice-create" className="hm-cta-btn"><FiPlus size={14}/> New Invoice</Link>
              </div>
            </div>

            {/* ── KPI Row ── */}
            <div className="hm-sec-lbl">Key Performance Indicators</div>
            <div className="hm-kpi-grid">
              {[
                { label:"Cash on Hand",       val:"₹12,45,000", trend:"+8.4%",  up:true,  icon:<FiDollarSign/>,  accent:"teal",   kpiColor:"teal" },
                { label:"Accounts Receivable",val:"₹2,10,430",  trend:"-3.1%",  up:false, icon:<FiClock/>,       accent:"red",    kpiColor:"danger" },
                { label:"Active CRM Leads",   val:"842",         trend:"+12%",   up:true,  icon:<FiUsers/>,       accent:"purple", kpiColor:"purple" },
                { label:"Stock Valuation",    val:"₹45,20,000", trend:"+5.7%",  up:true,  icon:<FiLayers/>,      accent:"blue",   kpiColor:"blue" },
              ].map((k, i) => (
                <div key={i} className="hm-card hm-kpi" style={{ animationDelay: `${i * 0.08}s` }}>
                  <div className={`hm-kpi-accent ${k.accent}`}/>
                  <div className={`hm-kpi-icon ${k.kpiColor}`}>{k.icon}</div>
                  <div className="hm-kpi-label">{k.label}</div>
                  <div className="hm-kpi-val">{k.val}</div>
                  <div>
                    <span className={`hm-kpi-trend ${k.up ? "up" : "down"}`}>
                      {k.up ? <FiArrowUp size={10}/> : <FiArrowDown size={10}/>}
                      {k.trend}
                    </span>
                    <span className="hm-kpi-sub"> vs last month</span>
                  </div>
                </div>
              ))}
            </div>

            {/* ── Chart Row ── */}
            <div className="hm-sec-lbl">Analytics</div>
            <div className="hm-row hm-row-3-2" style={{ marginBottom: 20 }}>

              {/* Revenue Chart */}
              <div className="hm-card">
                <div className="hm-chart-header">
                  <div>
                    <div className="hm-chart-title">Revenue vs Expenditure</div>
                    <div className="hm-chart-sub">Weekly performance overview</div>
                  </div>
                  <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                    <span className="hm-live-badge"><span className="hm-live-badge-dot"/>Live</span>
                    <div className="hm-period-tabs">
                      {["Daily","Weekly","Monthly"].map(p => (
                        <button key={p} className={`hm-period-tab ${activePeriod===p?"active":""}`}
                          onClick={() => setActivePeriod(p)}>{p}</button>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="hm-insight-chip">
                  <FiZap size={11}/> AI Insight: Revenue projected to hit
                  <strong> ₹15L</strong> by Sunday based on current trend
                </div>
                <div className="hm-chart-body">
                  <ResponsiveContainer width="100%" height={280}>
                    <AreaChart data={performanceData} margin={{ top:4, right:8, left:0, bottom:0 }}>
                      <defs>
                        <linearGradient id="incomeGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%"  stopColor="#0a5556" stopOpacity={0.18}/>
                          <stop offset="95%" stopColor="#0a5556" stopOpacity={0.01}/>
                        </linearGradient>
                        <linearGradient id="expenseGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%"  stopColor="#14b8a6" stopOpacity={0.12}/>
                          <stop offset="95%" stopColor="#14b8a6" stopOpacity={0.01}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(10,85,86,0.07)"/>
                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize:12, fill:"#5a8080", fontFamily:"DM Sans" }}/>
                      <YAxis axisLine={false} tickLine={false} tick={{ fontSize:11, fill:"#5a8080", fontFamily:"DM Sans" }} tickFormatter={v=>`₹${v/1000}k`}/>
                      <Tooltip content={<CustomTooltip/>}/>
                      <Area type="monotone" dataKey="income"  name="Income"  stroke="#0a5556" strokeWidth={3} fillOpacity={1} fill="url(#incomeGrad)"  dot={{ r:4, fill:"#0a5556", strokeWidth:2, stroke:"#fff" }} activeDot={{ r:6 }}/>
                      <Area type="monotone" dataKey="expense" name="Expense" stroke="#14b8a6" strokeWidth={2} fillOpacity={1} fill="url(#expenseGrad)" dot={false} strokeDasharray="6 3"/>
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Expense Pie */}
              <div className="hm-card">
                <div className="hm-chart-header" style={{ marginBottom: 0 }}>
                  <div>
                    <div className="hm-chart-title">Expense Breakdown</div>
                    <div className="hm-chart-sub">By category, this month</div>
                  </div>
                </div>
                <div style={{ padding:"8px 0 0" }}>
                  <ResponsiveContainer width="100%" height={192}>
                    <PieChart>
                      <Pie data={categoryData} innerRadius={58} outerRadius={78} paddingAngle={4} dataKey="value" startAngle={90} endAngle={-270}>
                        {categoryData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} stroke="transparent"/>
                        ))}
                      </Pie>
                      <Tooltip formatter={(val) => [`${val}%`, "Share"]}/>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="hm-pie-legend">
                  {categoryData.map((item, i) => (
                    <div key={i} className="hm-pie-row">
                      <div className="hm-pie-name">
                        <div className="hm-pie-dot" style={{ background: item.color }}/>
                        {item.name}
                      </div>
                      <div className="hm-pie-bar-wrap">
                        <div className="hm-pie-bar" style={{ width:`${item.value}%`, background:item.color }}/>
                      </div>
                      <div className="hm-pie-pct">{item.value}%</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Transactions + Deadlines ── */}
            <div className="hm-sec-lbl">Activity</div>
            <div className="hm-row hm-row-3-4" style={{ marginBottom: 20 }}>

              {/* Deadlines */}
              <div className="hm-card">
                <div className="hm-chart-header">
                  <div>
                    <div className="hm-chart-title"><FiCalendar size={13} style={{ marginRight:6, verticalAlign:"middle" }}/>Upcoming Deadlines</div>
                    <div className="hm-chart-sub">Next 14 days</div>
                  </div>
                </div>
                <div className="hm-timeline" style={{ marginTop: 12 }}>
                  {deadlines.map((d, i) => (
                    <div key={i} className="hm-tl-item">
                      <div className="hm-tl-date">
                        <div className="hm-tl-day">{d.date}</div>
                        <div className="hm-tl-mon">{d.mon}</div>
                      </div>
                      <div className="hm-tl-divider"/>
                      <div className="hm-tl-body">
                        <div className="hm-tl-title">{d.title}</div>
                        <div className="hm-tl-meta">
                          <span className={`hm-tl-tag ${d.type}`}>{d.type.toUpperCase()}</span>
                          {d.desc}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ padding:"0 20px 18px" }}>
                  <button style={{ width:"100%", padding:"9px", border:"1.5px solid var(--border)", borderRadius:"10px", background:"transparent", color:"var(--t)", fontFamily:"'Sora',sans-serif", fontSize:"12.5px", fontWeight:700, cursor:"pointer" }}>
                    <FiCalendar size={12} style={{ marginRight:6, verticalAlign:"middle" }}/> Sync Calendar
                  </button>
                </div>
              </div>

              {/* Transactions */}
              <div className="hm-card">
                <div className="hm-chart-header">
                  <div>
                    <div className="hm-chart-title">Recent Transactions</div>
                    <div className="hm-chart-sub">Latest invoice activity</div>
                  </div>
                  <span className="hm-live-badge"><span className="hm-live-badge-dot"/>Live Updates</span>
                </div>
                <div className="hm-tbl-wrap" style={{ marginTop:12 }}>
                  <table className="hm-tbl">
                    <thead>
                      <tr>
                        <th>Document</th>
                        <th>Client</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Date</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((row, i) => (
                        <tr key={i}>
                          <td>
                            <span className="hm-inv-id">
                              <FiFileText size={11}/>{row.id}
                            </span>
                          </td>
                          <td>
                            <div className="hm-client-name">{row.name}</div>
                            <div className="hm-client-sub">{row.sub}</div>
                          </td>
                          <td><span className="hm-amount">{row.amt}</span></td>
                          <td>
                            <span className={`hm-status ${row.status}`}>
                              <span className="hm-status-ring"/>
                              {row.status.charAt(0).toUpperCase()+row.status.slice(1)}
                            </span>
                          </td>
                          <td style={{ color:"var(--muted)", fontSize:12 }}>{row.date}</td>
                          <td>
                            <button className="hm-tbl-action"><FiArrowUpRight size={13}/></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* ── Bottom Row: Tools + Health ── */}
            <div className="hm-sec-lbl">System</div>
            <div className="hm-row hm-row-4-4">

              {/* Smart Tools */}
              <div className="hm-card hm-tools-card">
                <div className="hm-tools-bg-circle hm-tools-bg-circle-1"/>
                <div className="hm-tools-bg-circle hm-tools-bg-circle-2"/>
                <div className="hm-tools-hd">
                  <div className="hm-tools-title">Smart Tools</div>
                  <div className="hm-tools-sub">One-click financial utilities to accelerate your workflow.</div>
                </div>
                <div className="hm-tools-grid">
                  {[
                    { label:"Tax Summary",   sub:"Q3 Ready",      icon:<FiTrendingUp/> },
                    { label:"Update Catalog",sub:"1,240 SKUs",     icon:<FiPackage/> },
                    { label:"Bank Sync",     sub:"Last: 10:45 AM", icon:<FiRefreshCw/> },
                    { label:"Team Access",   sub:"8 members",      icon:<FiUsers/> },
                  ].map((tool, i) => (
                    <div key={i} className="hm-tool-tile">
                      <div className="hm-tool-icon">{tool.icon}</div>
                      <div>
                        <div className="hm-tool-label">{tool.label}</div>
                        <div className="hm-tool-sub">{tool.sub}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* System Health */}
              <div className="hm-card">
                <div className="hm-chart-header">
                  <div>
                    <div className="hm-chart-title">System Health &amp; Intelligence</div>
                    <div className="hm-chart-sub">Real-time infrastructure metrics</div>
                  </div>
                </div>
                <div className="hm-health-grid">
                  {[
                    { label:"Server Latency",  val:"24ms",  sub:"Excellent",   icon:<FiZap size={14}/>,      color:"green" },
                    { label:"Data Backup",     val:"Secure",sub:"Encrypted",   icon:<FiShield size={14}/>,   color:"blue" },
                    { label:"API Throughput",  val:"1.2k/s",sub:"Stable",      icon:<FiCpu size={14}/>,      color:"green" },
                    { label:"DB Connections",  val:"98%",   sub:"Healthy",     icon:<FiDatabase size={14}/>, color:"blue" },
                  ].map((h, i) => (
                    <div key={i} className="hm-health-tile">
                      <div className="hm-health-label" style={{ display:"flex", alignItems:"center", gap:5 }}>
                        {h.icon} {h.label}
                      </div>
                      <div className={`hm-health-val ${h.color}`}>{h.val}</div>
                      <div className="hm-health-sub">{h.sub}</div>
                    </div>
                  ))}
                </div>
                <div className="hm-monitor">
                  <div className="hm-monitor-pulse"><div className="hm-monitor-dot"/></div>
                  <div className="hm-monitor-text">
                    <strong>Auto-reconciliation complete —</strong> 424 bank entries processed
                    successfully at <strong>10:45 AM</strong>. No discrepancies found.
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </main>
    </>
  );
}

export default Home;
